/**
 * Call controls — what the widget shows while a session is live.
 *
 * Two shapes, same as web: a text call gets the message input pill, an audio
 * call gets the mute + end-call pair. Nothing else lives here; sending goes
 * through `onSendMessage` (the core controller) and hanging up through
 * `onCallEnd`.
 *
 * The one deliberate divergence from web is where mic state comes from. Web
 * reads `localParticipant.isMicrophoneEnabled` through LiveKit's React context,
 * which costs a `RoomContext.Provider` wrapped around the whole tree. On RN the
 * widget already holds the Room — the transport hands it back — so the current
 * state and the toggle arrive as props instead: one provider fewer, and this
 * component keeps knowing nothing about transports. A caller that cannot offer
 * a toggle (no room yet) simply omits `onToggleMic` and gets no mute button
 * rather than a dead one.
 */

import { useState, type FC } from "react";
import { StyleSheet, View } from "react-native";
import { MicOff } from "lucide-react-native";
import type { RinggWidgetConfig, SlashCommand } from "@ringg/core";

import { solidColor } from "../lib/gradient";
import { MicrophoneIcon, PhoneEndCallIcon } from "../lib/icons";
import { toViewStyle } from "../lib/portable-styles";
import { ringgId } from "../lib/testing";
import { useWidgetTheme } from "../lib/widget-theme";
import MessageInput from "./message-input";
import { Touchable } from "./ui/pressable";

interface CallControlProps {
  isCalling: boolean;
  callMode: "text" | "audio";
  buttonConfig?: RinggWidgetConfig["buttons"];
  enabledSlashCommands?: SlashCommand[];
  onSlashCommand?: (command: SlashCommand) => void;
  onCallEnd?: () => void;
  // Fired the instant the user submits a text message (before the agent reply
  // arrives) so the transcript can show the typing indicator right away.
  onUserMessage?: () => void;
  /** Sends the message through the core controller (replaces useChat().send). */
  onSendMessage?: (message: string) => Promise<void>;
  /** Live mic state, owned by whoever owns the Room. Defaults to on. */
  micEnabled?: boolean;
  /** Flips the mic. Absent (no room yet) leaves the button inert, not hidden. */
  onToggleMic?: () => void;
}

interface AudioCallControlsProps {
  micEnabled: boolean;
  onToggleMic?: () => void;
  buttonConfig?: RinggWidgetConfig["buttons"];
  onCallEnd?: () => void;
}

/** Web's `h-5 w-5` on both control icons. */
const ICON_SIZE = 20;

/** Web's `text-white` on the end-call glyph — fixed, not themed. */
const END_CALL_ICON_COLOR = "#ffffff";

/**
 * Markup parity with web's AudioControlsView: a centred pair of 48dp circles,
 * no labels. Theme colours go through `solidColor` because RN rejects a
 * gradient string outright where the DOM would merely ignore it, and neither
 * `backgroundColor` nor `errorColor` is a gradient-bearing theme field.
 */
const AudioControlsView: FC<AudioCallControlsProps> = ({ micEnabled, onToggleMic, buttonConfig, onCallEnd }) => {
  const theme = useWidgetTheme();
  const handleCallEndTrigger = () => onCallEnd?.();
  const micStyles = toViewStyle(buttonConfig?.mic?.styles);

  return (
    <View testID={ringgId("call-controls")} style={styles.row}>
      {/* Always rendered, even with no room to toggle — web does the same with a
          no-op handler. A voice call that shows one control where the user
          expects two reads as a broken call, not as a degraded one. */}
      <Touchable
        testID={ringgId("mute-button")}
        // The muted flag has no attribute namespace to live in on RN; the
        // accessibility label carries the state, exactly as web's aria-label does.
        accessibilityLabel={micEnabled ? "Mute" : "Unmute"}
        onPress={onToggleMic ?? (() => {})}
        style={[styles.circle, styles.muteButton, { backgroundColor: solidColor(theme.backgroundColor), borderColor: solidColor(theme.borderColor) }, micStyles]}
      >
        {micEnabled ? <MicrophoneIcon size={ICON_SIZE} fill={solidColor(theme.textColor)} /> : <MicOff size={ICON_SIZE} color={solidColor(theme.textColor)} />}
      </Touchable>
      <Touchable
        testID={ringgId("end-call-button")}
        accessibilityLabel="End call"
        onPress={handleCallEndTrigger}
        // Source quirk kept: the mic style override spreads on BOTH buttons.
        style={[styles.circle, { backgroundColor: solidColor(theme.errorColor) }, micStyles]}
      >
        <PhoneEndCallIcon size={ICON_SIZE} fill={END_CALL_ICON_COLOR} />
      </Touchable>
    </View>
  );
};

const CallControls: FC<CallControlProps> = ({ isCalling, callMode, buttonConfig, enabledSlashCommands, onSlashCommand, onUserMessage, onCallEnd, onSendMessage, micEnabled = true, onToggleMic }) => {
  // Mirrors LiveKit useChat().isSending — true while a send is in flight.
  const [isSending, setIsSending] = useState(false);

  const handleSendMessage = async (message: string) => {
    // Surface the typing indicator immediately — a reply is always expected, so
    // we don't wait for the agent's stream to open.
    onUserMessage?.();
    setIsSending(true);
    try {
      await onSendMessage?.(message);
    } finally {
      setIsSending(false);
    }
  };

  if (!isCalling) return null;

  switch (callMode) {
    case "text":
      return <MessageInput isSending={isSending} enabledSlashCommands={enabledSlashCommands} onMessageSubmit={handleSendMessage} onSlashCommand={onSlashCommand} />;
    case "audio":
      return <AudioControlsView micEnabled={micEnabled} onToggleMic={onToggleMic} buttonConfig={buttonConfig} onCallEnd={onCallEnd} />;
    default:
      return null;
  }
};

export default CallControls;

const styles = StyleSheet.create({
  row: { flexDirection: "row", width: "100%", justifyContent: "center", alignItems: "center", gap: 24 },
  circle: { height: 48, width: 48, borderRadius: 9999, alignItems: "center", justifyContent: "center" },
  muteButton: { borderWidth: 1 },
});
